import React, { useState } from "react";
import { profile } from "../../mock";
import { toast } from "sonner";
import { Send, Mail, MapPin, Github, Linkedin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    const accessKey = process.env.REACT_APP_WEB3FORMS_KEY;
    if (!accessKey) {
      toast.error("Email service is not configured yet.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: accessKey,
          subject: `New portfolio message from ${form.name}`,
          from_name: "Portfolio Contact Form",
          name: form.name,
          email: form.email,
          message: form.message,
          botcheck: "",
        }),
      });
      const data = await res.json();
      if (data.success) {
        setForm({ name: "", email: "", message: "" });
        toast.success("Message sent. I'll get back to you soon.");
      } else {
        toast.error(data.message || "Failed to send. Please try again.");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 border-t border-zinc-900 overflow-hidden"
    >
      <div className="aurora top-10 left-1/3 opacity-20" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-6 md:px-8">
        <div className="flex items-center gap-3 mb-3 reveal">
          <span className="mono text-xs section-num font-semibold">05 //</span>
          <span className="mono text-xs text-zinc-500 uppercase tracking-[0.2em]">
            Contact
          </span>
        </div>
        <h2 className="display text-4xl md:text-5xl font-semibold reveal">
          Let&apos;s build{" "}
          <span className="text-zinc-500">something useful.</span>
        </h2>

        <div className="mt-12 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-6 reveal">
            <p className="text-zinc-400 leading-relaxed">
              If you have an AI idea ready to be built, or a role that calls for
              someone who can go from data to deployment? I&apos;d love to hear
              from you.
            </p>

            <div className="space-y-3">
              <InfoRow
                icon={Mail}
                label="Email"
                value={profile.email}
                href={profile.socials.email}
              />
              <InfoRow
                icon={Linkedin}
                label="LinkedIn"
                value="linkedin.com/in/azzam-saud"
                href={profile.socials.linkedin}
              />
              <InfoRow
                icon={Github}
                label="GitHub"
                value="github.com/Azzam-Saud"
                href={profile.socials.github}
              />
              <InfoRow icon={MapPin} label="Location" value={profile.location} />
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-3 border border-zinc-900 rounded-2xl p-6 md:p-8 bg-[#0b0b0d] reveal"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your name"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@email.com"
              />
            </div>
            <div className="mt-4">
              <label className="mono text-xs text-zinc-500 mb-2 block">
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={6}
                placeholder="Tell me a bit about your project or role..."
                className="w-full bg-[#09090b] border border-zinc-800 rounded-md p-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-[#5eead4]/60 focus:ring-2 focus:ring-[#5eead4]/20 transition-colors"
              />
            </div>
            <div className="mt-6 flex items-center justify-between">
              
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary-mint rounded-md px-5 py-2.5 text-sm font-medium mono inline-flex items-center gap-2 disabled:opacity-70"
              >
                {submitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send message <Send size={14} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="mono text-xs text-zinc-500 mb-2 block">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-[#09090b] border border-zinc-800 rounded-md p-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-[#5eead4]/60 focus:ring-2 focus:ring-[#5eead4]/20 transition-colors"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const Tag = href ? "a" : "div";
  return (
    <Tag
      href={href}
      target={href && href.startsWith("http") ? "_blank" : undefined}
      rel={href && href.startsWith("http") ? "noreferrer" : undefined}
      className="flex items-center gap-3 border border-zinc-900 rounded-xl p-4 card-hover bg-[#0b0b0d]"
    >
      <div className="h-9 w-9 rounded-md bg-[#5eead4]/10 text-[#5eead4] border border-[#5eead4]/20 grid place-items-center">
        <Icon size={16} />
      </div>
      <div>
        <div className="mono text-[11px] text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-200 break-all">{value}</div>
      </div>
    </Tag>
  );
}
